import React, { createContext, useContext, useState, ReactNode } from "react";

interface UserContextType {
  username: string;
  role: string;
  userId: string;
  serviceLine: string;
  rolename: string;
  isAdmin: boolean;
  hasShareholderAccess: boolean;
  isGrowthTeam: boolean;
  isGrowthLeader: boolean;
  isUserLoaded: boolean;
  leaderId: any;
  leaderEntraId: string;
  shareholderEntraID: string;
  leaderName: any;
  setUserInfo: (userInfo: {
    username: string;
    role: string;
    userId: string;
    rolename: string;
    isAdmin: boolean;
    hasShareholderAccess: boolean;
    isGrowthTeam: boolean;
    isGrowthLeader: boolean;
    serviceLine: string;
    leaderId: any;
    leaderEntraId: string;
    shareholderEntraID: string;
    leaderName: any;
  }) => void;
  /* Drops admin rights in-session, so the sidebar's Apex Admin entry and the
     AdminRoute guard both fall away once an admin endpoint answers 403. */
  clearAdminAccess: () => void;
}

// Create context with proper default value (cast to type)
const UserContext = createContext<UserContextType>({
  username: "",
  role: "",
  userId: "",
  serviceLine: "",
  rolename: "",
  isAdmin: false,
  hasShareholderAccess: false,
  isGrowthTeam: false,
  isGrowthLeader: false,
  isUserLoaded: false,
  leaderId: null,
  leaderEntraId: "",
  shareholderEntraID: "",
  leaderName: null,
  setUserInfo: () => {}, // default noop
  clearAdminAccess: () => {}, // default noop
});

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("");
  const [userId, setUserId] = useState("");
  const [serviceLine, setServiceLine] = useState("");
  const [rolename, setRolename] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasShareholderAccess, setHasShareholderAccess] = useState(false);
  const [isGrowthTeam, setIsGrowthTeam] = useState(false);
  const [isGrowthLeader, setIsGrowthLeader] = useState(false);
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [leaderId, setLeaderId] = useState(null);
  const [leaderEntraId, setLeaderEntraId] = useState("");
  const [shareholderEntraID, setShareholderEntraID] = useState("");
  const [leaderName, setLeaderName] = useState(null);

  const setUserInfo = ({
    username,
    role,
    userId,
    serviceLine,
    rolename,
    isAdmin,
    hasShareholderAccess,
    isGrowthTeam,
    isGrowthLeader,
    leaderId,
    leaderEntraId,
    shareholderEntraID,
    leaderName,
  }: {
    username: string;
    role: string;
    userId: string;
    serviceLine: string;
    rolename: string;
    isAdmin: boolean;
    hasShareholderAccess: boolean;
    isGrowthTeam: boolean;
    isGrowthLeader: boolean;
    leaderId: any;
    leaderName: any;
    leaderEntraId: string;
    shareholderEntraID: string;
  }) => {
    setUsername(username);
    setRole(role);
    setUserId(userId);
    setServiceLine(serviceLine);
    setRolename(rolename);
    setIsAdmin(isAdmin);
    setHasShareholderAccess(hasShareholderAccess);
    setIsGrowthTeam(isGrowthTeam);
    setIsGrowthLeader(isGrowthLeader);
    setIsUserLoaded(true);
    setLeaderId(leaderId);
    setLeaderEntraId(leaderEntraId);
    setShareholderEntraID(shareholderEntraID);
    setLeaderName(leaderName);
  };

  const clearAdminAccess = () => setIsAdmin(false);

  return (
    <UserContext.Provider
      value={{
        username,
        role,
        userId,
        serviceLine,
        rolename,
        isAdmin,
        hasShareholderAccess,
        isGrowthTeam,
        isGrowthLeader,
        isUserLoaded,
        leaderId,
        leaderName,
        leaderEntraId,
        shareholderEntraID,
        setUserInfo,
        clearAdminAccess,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Custom hook for cleaner access
export const useUserContext = () => useContext(UserContext);
